import type { PhotoUpload } from '@escala/contracts';
import { MAX_PHOTO_BYTES, PHOTO_SIZE, type PhotoType } from '@escala/domain/photo';

/**
 * Foto do membro no navegador: abre a imagem escolhida, recorta o quadrado que a pessoa
 * enquadrou e reduz para `PHOTO_SIZE` px. Redesenhar no canvas também descarta os
 * metadados do arquivo (o EXIF, com a localização de onde a foto foi tirada) — só os
 * pixels chegam ao servidor. As regras (tipos, 2 MB, 64 KB) são de `@escala/domain/photo`.
 */

export interface PhotoSource {
  /** O que o canvas desenha: já na orientação certa (o `createImageBitmap` lê o EXIF). */
  image: CanvasImageSource;
  width: number;
  height: number;
}

/** Abre a imagem escolhida; `null` quando o navegador não consegue decodificá-la. */
export async function openPhoto(file: Blob): Promise<PhotoSource | null> {
  if (typeof createImageBitmap !== 'function') return openWithImage(file);

  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });

    return { image: bitmap, width: bitmap.width, height: bitmap.height };
  } catch {
    return openWithImage(file);
  }
}

/** Sem `createImageBitmap` (ou se ele recusar): a `<img>`, que também segue o EXIF. */
function openWithImage(file: Blob): Promise<PhotoSource | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    const done = (source: PhotoSource | null) => {
      URL.revokeObjectURL(url);
      resolve(source);
    };

    image.onload = () =>
      done(image.naturalWidth ? { image, width: image.naturalWidth, height: image.naturalHeight } : null);
    image.onerror = () => done(null);
    image.src = url;
  });
}

// ===== Enquadramento =====

/** O enquadramento: zoom (1 = a imagem cobre o círculo) e o deslocamento, em px da tela. */
export interface Framing {
  zoom: number;
  x: number;
  y: number;
}

export const MIN_ZOOM = 1;
export const MAX_ZOOM = 4;

/** A escala da imagem na tela: o menor lado cobre o visor, vezes o zoom. */
export function scaleOf(
  source: Pick<PhotoSource, 'width' | 'height'>,
  viewport: number,
  zoom: number
): number {
  return (viewport / Math.min(source.width, source.height)) * zoom;
}

/** Imagem centralizada, cobrindo o visor. */
export function initialFraming(source: Pick<PhotoSource, 'width' | 'height'>, viewport: number): Framing {
  const scale = scaleOf(source, viewport, MIN_ZOOM);

  return {
    zoom: MIN_ZOOM,
    x: (viewport - source.width * scale) / 2,
    y: (viewport - source.height * scale) / 2,
  };
}

/** O enquadramento dentro dos limites: zoom entre 1 e 4 e o visor sempre coberto. */
export function clampFraming(
  source: Pick<PhotoSource, 'width' | 'height'>,
  viewport: number,
  framing: Framing
): Framing {
  const zoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, framing.zoom));
  const scale = scaleOf(source, viewport, zoom);
  const clamp = (value: number, size: number) => Math.min(0, Math.max(viewport - size * scale, value));

  return { zoom, x: clamp(framing.x, source.width), y: clamp(framing.y, source.height) };
}

/** Muda o zoom mantendo parado o centro do visor. */
export function zoomTo(
  source: Pick<PhotoSource, 'width' | 'height'>,
  viewport: number,
  framing: Framing,
  zoom: number
): Framing {
  const ratio = scaleOf(source, viewport, zoom) / scaleOf(source, viewport, framing.zoom);
  const center = viewport / 2;

  return clampFraming(source, viewport, {
    zoom,
    x: center - (center - framing.x) * ratio,
    y: center - (center - framing.y) * ratio,
  });
}

/** O quadrado da imagem original (em px dela) que aparece no visor. */
export function cropOf(source: Pick<PhotoSource, 'width' | 'height'>, viewport: number, framing: Framing) {
  const scale = scaleOf(source, viewport, framing.zoom);

  // `0 - x`, não `-x`: o enquadramento na borda dá 0, não -0.
  return { x: (0 - framing.x) / scale, y: (0 - framing.y) / scale, size: viewport / scale };
}

// ===== Redução =====

/** Qualidades tentadas, da melhor para a menor, até a foto caber em `MAX_PHOTO_BYTES`. */
const QUALITIES = [0.86, 0.75, 0.6, 0.45];

/**
 * Recorta e reduz para o quadrado de `PHOTO_SIZE` px, em WebP (ou JPEG, no navegador que
 * não gera WebP). `null` quando o navegador não tem canvas ou a foto não coube.
 */
export async function renderPhoto(
  source: PhotoSource,
  crop: { x: number; y: number; size: number }
): Promise<PhotoUpload | null> {
  const canvas = document.createElement('canvas');

  canvas.width = PHOTO_SIZE;
  canvas.height = PHOTO_SIZE;

  const ctx = canvas.getContext('2d');

  if (!ctx) return null;

  // Fundo branco: a transparência do PNG viraria preto no JPEG.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, PHOTO_SIZE, PHOTO_SIZE);
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source.image, crop.x, crop.y, crop.size, crop.size, 0, 0, PHOTO_SIZE, PHOTO_SIZE);

  for (const type of ['image/webp', 'image/jpeg'] as const) {
    for (const quality of QUALITIES) {
      const blob = await toBlob(canvas, type, quality);

      // O navegador que não gera o tipo pedido devolve PNG: tenta o próximo.
      if (!blob || blob.type !== type) break;
      if (blob.size <= MAX_PHOTO_BYTES) return { type, data: await toBase64(blob) };
    }
  }

  return null;
}

function toBlob(canvas: HTMLCanvasElement, type: PhotoType, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

/** O base64 do arquivo, sem o prefixo `data:`. */
export function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ''));
    reader.onerror = () => reject(reader.error ?? new Error('Falha ao ler a foto.'));
    reader.readAsDataURL(blob);
  });
}
